<script setup lang="ts">
import {computed} from 'vue';
import VChart from 'vue-echarts';
import type {EChartsOption, SeriesOption} from 'echarts';
import '@/base/echarts.js';
import {FaseStatistiek, InitiatiefFaseEnum_name, KLEUR_BALK, KLEUR_LIJN} from 'common';

const KLEUR_BOX: string = 'rgba(164,193,48,0.35)';

const props = defineProps<{
    statistiek: FaseStatistiek[];
}>();

// [min, q1, median, q3, max] without the outliers above the fence
function box(s: FaseStatistiek): number[] {
    const binnen: number[] = s.waarden.filter(v => v <= s.grens);
    return [Math.min(...binnen, s.q1), s.q1, s.mediaan, s.q3, Math.max(...binnen, s.q3)];
}

function punten(f: (s: FaseStatistiek) => number | undefined): number[][] {
    return props.statistiek.flatMap((s, i) => {
        const v: number | undefined = f(s);
        return v === undefined ? [] : [[v, i]];
    });
}

const option = computed<EChartsOption>(() => ({
    grid   : {left: 8, right: 24, top: 24, bottom: 8, containLabel: true},
    legend : {top: 0},
    tooltip: {trigger: 'item'},
    xAxis  : {type: 'value', name: 'kwartalen', nameLocation: 'middle', nameGap: 24, minInterval: 1},
    yAxis  : {type: 'category', inverse: true, data: props.statistiek.map(s => InitiatiefFaseEnum_name(s.fase))},
    series : [
        {type: 'boxplot', name: 'verblijftijd', data: props.statistiek.map(s => s.waarden.length === 0 ? [] : box(s)), itemStyle: {color: KLEUR_BOX, borderColor: KLEUR_LIJN}} as unknown as SeriesOption,
        {type: 'scatter', name: 'uitschieters', symbolSize: 8, itemStyle: {color: KLEUR_BALK.rood}, data: props.statistiek.flatMap((s, i) => s.waarden.filter(v => v > s.grens).map(v => [v, i]))} as unknown as SeriesOption,
        {type: 'scatter', name: 'gemiddelde', symbol: 'triangle', symbolSize: 13, itemStyle: {color: KLEUR_BALK.amber}, data: punten(s => s.waarden.length === 0 ? undefined : s.gemiddelde)} as unknown as SeriesOption,
        {type: 'scatter', name: 'org. mediaan', symbol: 'rect', symbolSize: [3, 22], itemStyle: {color: KLEUR_BALK.groen}, data: punten(s => s.organisatieMediaan)} as unknown as SeriesOption,
    ],
}));
</script>

<template>
    <v-chart :option="option" autoresize :style="{height: '260px', width: '100%'}"/>
</template>
